/**
 * IONLOOP Assistant — context resolver (follow-up + pronoun + ellipsis).
 *
 * Deterministically decides whether a new user question is standalone or
 * contextual, resolves pronouns/ellipsis against the conversation timeline,
 * detects topic switches and produces clarification when confidence is low.
 *
 * Never guesses: if resolution confidence is too low the caller is told to ask
 * a clarification question instead.
 */

var tokenizer = require('../knowledge/tokenizer');
var normalizer = require('../knowledge/query-normalizer');

var PRONOUNS = ['it', 'that', 'this', 'those', 'one', 'them', 'its', 'this one', 'that one', 'that feature', 'it secure'];
var RESET_COMMANDS = [/^(start over|new topic|clear chat|clear conversation|reset|restart|switch topic)\b/i];
var REFINEMENT_SHORT = [/^(explain|say|put) (it )?simpler\b/i, /^(explain|make) (it )?shorter\b/i, /^(tl;?dr|summary|summarize|brief)\b/i, /^shorter\b/i, /^simpler\b/i];
var REFINEMENT_DETAIL = [/^(more detail|give me more detail|give more detail|expand|elaborate|go deeper|more info|tell me more about it|in detail)\b/i, /^more detail\b/i, /^elaborate\b/i, /^expand\b/i];
var TOPIC_SWITCH = [/^(now tell me about|now let'?s talk about|let'?s talk about|let'?s switch|switch (to|topic)|different question|another topic|change topic|now (about|talk))\b/i];
var FOLLOWUP_PREFIX = /^(what about|how about|and|and what about|also|what about the|and the|how about the)\s+(.+)$/i;

function hasAnyToken(raw, list) {
    var tokens = tokenizer.tokenize(raw);
    return tokens.some(function (t) { return list.indexOf(t) !== -1; });
}

function escapeRe(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function containsPronoun(raw) {
    var lower = ' ' + String(raw || '').toLowerCase() + ' ';
    return PRONOUNS.some(function (p) {
        return new RegExp('\\b' + escapeRe(p) + '\\b', 'i').test(lower);
    });
}

/**
 * Resolve the question against the conversation timeline.
 *
 * Returns:
 *  { status: 'standalone'|'contextual'|'refinement'|'action'|'reset'|'clarify',
 *    resolvedQuery, context, mode, confidence, clarificationCandidates }
 */
function resolveQuestion(rawQuestion, conversation, graph) {
    var query = String(rawQuestion || '').trim();
    if (!query) {
        return { status: 'standalone', resolvedQuery: query, context: null, confidence: 0 };
    }

    var normalized = normalizer.normalizeQuery(query);
    var hasContext = conversation && conversation.getExchanges().length > 0;
    var primaryEntity = hasContext ? conversation.getPrimaryEntity() : null;
    var recentCategory = hasContext ? conversation.getRecentCategory() : null;
    var comparisonEntities = hasContext ? conversation.getComparisonEntities() : [];
    var context = hasContext ? conversation.buildContext() : null;

    // 1) Reset commands.
    if (RESET_COMMANDS.some(function (rx) { return rx.test(query); })) {
        return { status: 'reset', resolvedQuery: query, context: null, confidence: 1 };
    }

    // 2) Refinement commands reuse the previous retrieval.
    if (hasContext && REFINEMENT_SHORT.some(function (rx) { return rx.test(query); })) {
        return { status: 'refinement', resolvedQuery: query, mode: 'short', context: context, confidence: 1 };
    }
    if (hasContext && REFINEMENT_DETAIL.some(function (rx) { return rx.test(query); })) {
        return { status: 'refinement', resolvedQuery: query, mode: 'detail', context: context, confidence: 1 };
    }

    // 3) Contextual action ("Open it", "take me there").
    if (hasContext && /^(open|take me to|go to|show me|navigate to)\s+(it|there|that)\s*$/i.test(query)) {
        var lastAnswer = conversation.getLastAnswer && conversation.getLastAnswer();
        if (lastAnswer && lastAnswer.actions && lastAnswer.actions.length) {
            return {
                status: 'action',
                resolvedQuery: query,
                action: lastAnswer.actions[0],
                context: context,
                confidence: 0.9
            };
        }
    }

    // 4) Topic switch detection — new explicit topic, drop context boost.
    var switched = TOPIC_SWITCH.some(function (rx) { return rx.test(query); });
    if (switched) {
        var stripped = query.replace(/^(now tell me about|now let'?s talk about|let'?s talk about|switch to|switch topic|different question|another topic|change topic|now about|now talk about|now)\s+/i, '').trim();
        return {
            status: 'standalone',
            resolvedQuery: stripped || query,
            context: { active: false },
            confidence: 0.8,
            topicSwitch: true
        };
    }

    // 5) Pronoun / follow-up resolution.
    if (hasContext && containsPronoun(query)) {
        var resolution = resolvePronoun(query, primaryEntity, comparisonEntities, recentCategory, context, conversation);
        if (resolution) {
            return resolution;
        }
        // Pronoun present but primary entity unknown → clarification.
        return {
            status: 'clarify',
            resolvedQuery: query,
            context: context,
            confidence: 0.3,
            clarificationCandidates: conversation.getRecentTopics().slice(0, 3)
        };
    }

    // 6) "what about X / and X / how about X / also X".
    var follow = query.match(FOLLOWUP_PREFIX);
    if (hasContext && follow) {
        var subject = follow[2].trim();
        var entity = graph ? graph.findEntity(subject) : null;

        // Comparison continuation: "what about futures" after "spot vs margin".
        if (comparisonEntities.length && comparisonEntities.some(function (e) {
            return e.toLowerCase() === subject.toLowerCase();
        })) {
            return {
                status: 'contextual',
                resolvedQuery: subject,
                context: context,
                confidence: 0.85,
                comparisonContinuation: true
            };
        }

        if (entity) {
            return {
                status: 'contextual',
                resolvedQuery: subject,
                context: context,
                confidence: 0.8,
                entity: entity.name
            };
        }

        // Ellipsis: "what about futures" inherits the previous category.
        if (recentCategory) {
            return {
                status: 'contextual',
                resolvedQuery: subject,
                context: context,
                confidence: 0.65,
                categoryContinuation: recentCategory
            };
        }
    }

    // 7) Bare continuation ("and", "also", "what about it?") with weak context.
    if (hasContext && /^(and|also|what about|how about|and more|anything else|more)\s*$/.test(query)) {
        if (primaryEntity) {
            return {
                status: 'contextual',
                resolvedQuery: 'Tell me more about ' + primaryEntity,
                context: context,
                confidence: 0.7
            };
        }
        return { status: 'clarify', resolvedQuery: query, context: context, confidence: 0.3, clarificationCandidates: conversation.getRecentTopics().slice(0, 3) };
    }

    // 8) Default: standalone question.
    return {
        status: 'standalone',
        resolvedQuery: query,
        context: context && !switched ? context : null,
        confidence: 1
    };
}

/**
 * Resolve a pronoun-referencing question to the previous primary entity.
 */
function resolvePronoun(query, primaryEntity, comparisonEntities, recentCategory, context, conversation) {
    if (!primaryEntity) {
        return null;
    }
    var pronouns = PRONOUNS.filter(function (p) {
        return new RegExp('\\b' + escapeRe(p) + '\\b', 'i').test(query);
    });
    if (pronouns.length === 0) {
        return null;
    }

    // If the previous turns are about a single clear entity, high confidence.
    var lastExchanges = conversation.getExchanges().slice(-3);
    var recentPrimary = lastExchanges.map(function (ex) { return ex.primaryEntity; }).filter(Boolean);
    var distinctPrimary = {};
    recentPrimary.forEach(function (e) { distinctPrimary[e] = true; });
    var distinctCount = Object.keys(distinctPrimary).length;
    var confidence = distinctCount <= 1 ? 0.93 : (distinctCount === 2 ? 0.6 : 0.45);

    // Expand "how does it work" → "how does <entity> work".
    var resolved = query.replace(
        new RegExp('\\b(' + pronouns.map(escapeRe).join('|') + ')\\b', 'ig'),
        primaryEntity
    );

    return {
        status: 'contextual',
        resolvedQuery: resolved,
        context: context,
        confidence: confidence,
        pronounResolution: true,
        resolvedEntity: primaryEntity
    };
}

module.exports = {
    resolveQuestion: resolveQuestion,
    containsPronoun: containsPronoun
};
